import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SparklesIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import SchemaSelectionComponent from '../../components/metadata/SchemaSelectionComponent';

/**
 * OnboardingSchemaSelectionPage - Schema-first table selection for onboarding
 *
 * Features:
 * - Schema-first selection for efficient management
 * - Smart recommendations based on data patterns
 * - Quick start auto-selection
 * - Progress tracking
 */
const OnboardingSchemaSelectionPage = () => {
  const navigate = useNavigate();
  const [discoveryResult, setDiscoveryResult] = useState(null);
  const [selectedSchemas, setSelectedSchemas] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load discovery results from previous step
    const stored = localStorage.getItem('discoveryResult');
    if (stored) {
      const result = JSON.parse(stored);
      setDiscoveryResult(result);

      // Auto-select recommended schemas
      const schemas = groupTablesBySchema(result.all_tables || result.tables || []);
      const recommendedSchemas = getRecommendedSchemas(schemas);
      setSelectedSchemas(recommendedSchemas.filter(s => s.recommended).map(s => s.name));
    }
  }, []);

  // Group tables by database.schema
  const groupTablesBySchema = (tables) => {
    const schemaMap = {};

    tables.forEach(table => {
      const database = table.database || 'UNKNOWN_DB';
      const schema = table.schema || 'UNKNOWN_SCHEMA';
      const fullSchemaName = `${database}.${schema}`;

      if (!schemaMap[fullSchemaName]) {
        schemaMap[fullSchemaName] = {
          name: fullSchemaName,
          database: database,
          schema: schema,
          tables: [],
          table_count: 0,
          total_rows: 0,
          size_bytes: 0
        };
      }

      schemaMap[fullSchemaName].tables.push(table);
      schemaMap[fullSchemaName].table_count += 1;
      schemaMap[fullSchemaName].total_rows += table.row_count || 0;
      schemaMap[fullSchemaName].size_bytes += table.size_bytes || 0;
    });

    return Object.values(schemaMap);
  };

  // Recommend schemas based on business value
  const getRecommendedSchemas = (schemas) => {
    return schemas.map(schemaObj => {
      let score = 0;

      // Score based on table count (more tables = more important)
      score += Math.min(schemaObj.table_count * 10, 50);

      // Score based on data volume
      if (schemaObj.total_rows > 1000000) score += 30;
      else if (schemaObj.total_rows > 100000) score += 20;
      else if (schemaObj.total_rows > 10000) score += 10;

      // Score based on naming patterns (check schema part only, not database)
      const schemaName = schemaObj.schema ? schemaObj.schema.toLowerCase() : '';
      const databaseName = schemaObj.database ? schemaObj.database.toLowerCase() : '';
      const highValuePatterns = ['prod', 'main', 'core', 'public', 'sales', 'customer', 'user'];
      const mediumValuePatterns = ['staging', 'analytics', 'reporting', 'marketing'];

      // Check both schema and database names for patterns
      const checkName = `${databaseName} ${schemaName}`;
      if (highValuePatterns.some(p => checkName.includes(p))) {
        score += 40;
      } else if (mediumValuePatterns.some(p => checkName.includes(p))) {
        score += 20;
      }

      return {
        ...schemaObj,
        score,
        recommended: score >= 50
      };
    }).sort((a, b) => b.score - a.score);
  };

  // Calculate cost savings
  const costSavings = useMemo(() => {
    if (!discoveryResult) return null;

    const allTables = discoveryResult.all_tables || discoveryResult.tables || [];
    const schemas = groupTablesBySchema(allTables);
    const selectedSchemaObjects = schemas.filter(s => selectedSchemas.includes(s.name));
    const tableCount = selectedSchemaObjects.reduce((sum, s) => sum + s.table_count, 0);

    // Estimate: Schema-level operations are ~50% more efficient
    const tableLevelCost = tableCount * 100; // $100 per table per month (example)
    const schemaLevelCost = tableLevelCost * 0.52; // 48% savings
    const savings = tableLevelCost - schemaLevelCost;
    const savingsPercent = Math.round((savings / tableLevelCost) * 100);

    return {
      tableLevelCost,
      schemaLevelCost,
      savings,
      savingsPercent,
      tableCount
    };
  }, [discoveryResult, selectedSchemas]);

  const handleQuickStart = () => {
    if (!discoveryResult) return;

    const schemas = groupTablesBySchema(discoveryResult.all_tables || discoveryResult.tables || []);
    const recommended = getRecommendedSchemas(schemas);
    const recommendedNames = recommended.filter(s => s.recommended).map(s => s.name);

    setSelectedSchemas(recommendedNames);
  };

  const handleContinue = () => {
    // Include all tables from selected schemas (database.schema format)
    const allTables = discoveryResult.all_tables || discoveryResult.tables || [];
    const tablesToSave = allTables
      .filter(table => {
        const database = table.database || 'UNKNOWN_DB';
        const schema = table.schema || 'UNKNOWN_SCHEMA';
        const fullSchemaName = `${database}.${schema}`;
        return selectedSchemas.includes(fullSchemaName);
      })
      .map(table => table.full_name || `${table.database}.${table.schema}.${table.name}`);

    // Save selections
    localStorage.setItem('selectedTables', JSON.stringify(tablesToSave));
    localStorage.setItem('selectedSchemas', JSON.stringify(selectedSchemas));
    // Note: No longer storing selectionMode - schema-first is the only mode

    navigate('/onboarding/validation-generation');
  };

  if (!discoveryResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Loading discovery results...</h2>
        </div>
      </div>
    );
  }

  const allTables = discoveryResult.all_tables || discoveryResult.tables || [];
  const schemas = groupTablesBySchema(allTables);
  const recommendedSchemas = getRecommendedSchemas(schemas);

  // Extract unique databases
  const databases = [...new Set(schemas.map(s => s.database))].sort();

  // Filter schemas by selected database
  const filteredSchemas = selectedDatabase === 'all'
    ? recommendedSchemas
    : recommendedSchemas.filter(s => s.database === selectedDatabase);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-6xl w-full bg-white rounded-lg shadow-xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <SparklesIcon className="h-12 w-12 text-primary-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Smart Schema Selection
          </h1>
          <p className="text-lg text-gray-600">
            Select schemas to monitor efficiently - manage thousands of tables with ease
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
            <span>Step 4 of 6</span>
            <span>5 minutes remaining</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-primary-600 h-2 rounded-full transition-all duration-300" style={{ width: '66.6%' }}></div>
          </div>
        </div>

        {/* Database Filter */}
        {databases.length > 1 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Filter by Database</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedDatabase('all')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  selectedDatabase === 'all'
                    ? 'bg-blue-100 text-blue-800 border-blue-300'
                    : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                }`}
              >
                All Databases
              </button>
              {databases.map(database => (
                <button
                  key={database}
                  onClick={() => setSelectedDatabase(database)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    selectedDatabase === database
                      ? 'bg-blue-100 text-blue-800 border-blue-300'
                      : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {database} ({schemas.filter(s => s.database === database).length} schemas)
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quick Start Banner */}
        {selectedSchemas.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <SparklesIcon className="h-6 w-6 text-yellow-600" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Quick Start Recommended
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                  We've identified {filteredSchemas.filter(s => s.recommended).length} high-value schemas based on your data
                </p>
              </div>
            </div>
            <button
              onClick={handleQuickStart}
              className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 transition-colors"
            >
              <SparklesIcon className="h-4 w-4 mr-2" />
              Auto-Select
            </button>
          </div>
        )}


        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{filteredSchemas.length}</div>
            <div className="text-sm text-blue-800">{selectedDatabase === 'all' ? 'Total' : 'Filtered'} Schemas</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {filteredSchemas.reduce((sum, s) => sum + s.table_count, 0)}
            </div>
            <div className="text-sm text-green-800">Tables</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {filteredSchemas.filter(s => s.recommended).length}
            </div>
            <div className="text-sm text-yellow-800">Recommended</div>
          </div>
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-primary-600">{selectedSchemas.length}</div>
            <div className="text-sm text-primary-800">Selected</div>
          </div>
        </div>

        {/* Schema Selection Component */}
        <div className="mb-8">
          <SchemaSelectionComponent
            connectionId={discoveryResult.connection_id}
            selectedSchemas={selectedSchemas}
            onSelectionChange={setSelectedSchemas}
            showMetadata={true}
            multiSelect={true}
            schemas={filteredSchemas}
            loading={false}
          />
        </div>

        {/* Selection Summary */}
        {selectedSchemas.length > 0 && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircleIcon className="h-5 w-5 text-primary-600" />
              <h3 className="text-sm font-semibold text-gray-900">Selection Summary</h3>
            </div>
            <div className="text-sm text-gray-700">
              You've selected <strong>{selectedSchemas.length}</strong> schema{selectedSchemas.length !== 1 ? 's' : ''} containing{' '}
              <strong>{costSavings?.tableCount || 0}</strong> table{costSavings?.tableCount !== 1 ? 's' : ''}. All tables in these schemas will be monitored.
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center pt-6 border-t border-gray-200">
          <button
            onClick={() => navigate('/onboarding/connection')}
            className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md shadow-sm hover:bg-gray-50 transition-colors"
          >
            Back
          </button>

          <div className="flex items-center space-x-4">
            {selectedSchemas.length === 0 ? (
              <span className="text-sm text-gray-500">
                Select at least one schema to continue
              </span>
            ) : (
              <span className="text-sm text-gray-600">
                {selectedSchemas.length} schema{selectedSchemas.length !== 1 ? 's' : ''} selected
              </span>
            )}

            <button
              onClick={handleContinue}
              disabled={selectedSchemas.length === 0}
              className="px-6 py-3 bg-primary-600 text-white font-medium rounded-md shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Continue to Validation Setup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingSchemaSelectionPage;
